<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Course Registration</h1>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <b-form-select
                v-model="filters.settings_id"
                :options="settingsOptions"
                @change="loadRegistrations"
              ></b-form-select>
            </div>
            <div class="col-md-3">
              <b-form-select
                v-model="filters.department_id"
                :options="departmentOptions"
                @change="loadRegistrations"
              ></b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select
                v-model="filters.reg_status"
                :options="statusOptions"
                @change="loadRegistrations"
              ></b-form-select>
            </div>
            <div class="col-md-3">
              <b-form-input
                v-model="filters.search"
                placeholder="Search student name, email, matric..."
                @keyup.enter="loadRegistrations"
              ></b-form-input>
            </div>
            <div class="col-md-1">
              <button class="btn btn-info btn-block" @click="loadRegistrations" :disabled="loading">
                <b-spinner small v-if="loading"></b-spinner>
                <b-icon icon="search" v-else></b-icon>
              </button>
            </div>
          </div>

          <b-table
            :items="registrations"
            :fields="fields"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading registrations...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No registrations found. Use filters to search.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(student_name)="data">
              {{ data.item.surname }} {{ data.item.first_name }}
            </template>
            <template #cell(session_semester)="data">
              {{ data.item.session_name }} / {{ data.item.semester_name }}
            </template>
            <template #cell(reg_status)="data">
              <b-badge :variant="data.value == '1' ? 'success' : 'warning'">
                {{ data.value == '1' ? 'Approved' : 'Pending' }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-outline-info mr-1" @click="viewCourses(data.item)" title="View courses">
                <b-icon icon="eye"></b-icon>
              </button>
              <button
                v-if="data.item.reg_status != '1'"
                class="btn btn-sm btn-success mr-1"
                @click="approveReg(data.item)"
                :disabled="actionId === data.item.id"
                title="Approve"
              >
                <b-spinner small v-if="actionId === data.item.id && actionType === 'approve'"></b-spinner>
                <b-icon icon="check-circle" v-else></b-icon>
              </button>
              <button
                v-if="data.item.reg_status == '1'"
                class="btn btn-sm btn-warning mr-1"
                @click="disapproveReg(data.item)"
                :disabled="actionId === data.item.id"
                title="Disapprove"
              >
                <b-spinner small v-if="actionId === data.item.id && actionType === 'disapprove'"></b-spinner>
                <b-icon icon="x-circle" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="registrations.length > perPage">
            <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(registrations.length / perPage) }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="registrations.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- View Courses Modal -->
      <b-modal
        v-model="showCoursesModal"
        :title="coursesModalTitle"
        size="lg"
        ok-only
        ok-title="Close"
      >
        <div v-if="loadingCourses" class="text-center my-3">
          <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else-if="selectedCourses.length === 0" class="text-center text-muted py-3">
          No courses registered.
        </div>
        <div v-else>
          <div class="mb-2">
            <small class="text-muted">
              Session: <strong>{{ selectedRegInfo.session_name }}</strong> |
              Semester: <strong>{{ selectedRegInfo.semester_name }}</strong> |
              Level: <strong>Year {{ selectedRegInfo.level }}</strong> |
              Status: <b-badge :variant="selectedRegInfo.reg_status == '1' ? 'success' : 'warning'">{{ selectedRegInfo.reg_status == '1' ? 'Approved' : 'Pending' }}</b-badge>
            </small>
          </div>
          <b-table :items="selectedCourses" :fields="courseFields" responsive striped small>
            <template #cell(course_status)="data">
              <b-badge :variant="data.value === 'C' ? 'primary' : 'warning'">
                {{ data.value === 'C' ? 'Compulsory' : 'Elective' }}
              </b-badge>
            </template>
          </b-table>
          <div class="text-right">
            <strong>Total Units: {{ totalUnits }}</strong>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseRegistration',
  data() {
    return {
      registrations: [],
      settings: [],
      departments: [],
      loading: false,
      filters: {
        settings_id: null,
        department_id: null,
        reg_status: null,
        search: '',
      },
      currentPage: 1,
      perPage: 20,
      fields: [
        { key: 'index', label: '#' },
        { key: 'student_name', label: 'Student' },
        { key: 'matric_no', label: 'Matric No' },
        { key: 'email', label: 'Email' },
        { key: 'department_name', label: 'Department' },
        { key: 'session_semester', label: 'Session/Semester' },
        { key: 'level', label: 'Level' },
        { key: 'reg_status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      actionId: null,
      actionType: null,
      showCoursesModal: false,
      coursesModalTitle: '',
      selectedCourses: [],
      selectedRegInfo: {},
      loadingCourses: false,
      courseFields: [
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Description' },
        { key: 'course_unit', label: 'Unit' },
        { key: 'course_status', label: 'Status' },
      ],
    };
  },
  computed: {
    settingsOptions() {
      return [
        { value: null, text: 'All Sessions/Semesters' },
        ...this.settings.map(s => ({ value: s.id, text: s.session_name + ' / ' + s.semester_name }))
      ];
    },
    departmentOptions() {
      return [
        { value: null, text: 'All Departments' },
        ...this.departments.map(d => ({ value: d.id, text: d.department }))
      ];
    },
    statusOptions() {
      return [
        { value: null, text: 'All Status' },
        { value: '1', text: 'Approved' },
        { value: '0', text: 'Pending' },
      ];
    },
    totalUnits() {
      return this.selectedCourses.reduce((sum, c) => sum + (parseInt(c.course_unit) || 0), 0);
    },
  },
  created() {
    this.loadSettings();
    this.loadDepartments();
  },
  methods: {
    loadSettings() {
      axios.get('/admin/all-settings').then(res => {
        this.settings = res.data.settings || [];
      });
    },
    loadDepartments() {
      axios.get('/admin/departments').then(res => {
        this.departments = res.data.departments || [];
      });
    },
    loadRegistrations() {
      this.loading = true;
      this.currentPage = 1;
      const params = {};
      if (this.filters.settings_id) params.settings_id = this.filters.settings_id;
      if (this.filters.department_id) params.department_id = this.filters.department_id;
      if (this.filters.reg_status !== null && this.filters.reg_status !== '') params.reg_status = this.filters.reg_status;
      if (this.filters.search) params.search = this.filters.search;

      axios.get('/admin/course-registrations', { params })
        .then(res => { this.registrations = res.data.registrations || []; })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load registrations' }); })
        .finally(() => { this.loading = false; });
    },
    viewCourses(item) {
      this.coursesModalTitle = item.surname + ' ' + item.first_name + ' - Registered Courses';
      this.showCoursesModal = true;
      this.loadingCourses = true;
      this.selectedCourses = [];
      this.selectedRegInfo = {};

      axios.get('/admin/course-registrations/' + item.id + '/courses')
        .then(res => {
          this.selectedCourses = res.data.courses || [];
          this.selectedRegInfo = res.data.registration || {};
        })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load courses' }); })
        .finally(() => { this.loadingCourses = false; });
    },
    approveReg(item) {
      this.actionId = item.id;
      this.actionType = 'approve';
      axios.patch('/admin/course-registrations/' + item.id + '/approve')
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Approved' });
          item.reg_status = '1';
        })
        .catch(err => {
          const msg = err.response && err.response.data && err.response.data.message;
          Toast.fire({ icon: 'error', title: msg || 'Failed to approve' });
        })
        .finally(() => { this.actionId = null; this.actionType = null; });
    },
    disapproveReg(item) {
      Swal.fire({
        title: 'Disapprove Registration?',
        text: 'This will allow the student to re-submit their course registration.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, disapprove',
      }).then(result => {
        if (result.isConfirmed) {
          this.actionId = item.id;
          this.actionType = 'disapprove';
          axios.patch('/admin/course-registrations/' + item.id + '/disapprove')
            .then(res => {
              Toast.fire({ icon: 'success', title: res.data.message || 'Disapproved' });
              item.reg_status = '0';
            })
            .catch(err => {
              const msg = err.response && err.response.data && err.response.data.message;
              Toast.fire({ icon: 'error', title: msg || 'Failed to disapprove' });
            })
            .finally(() => { this.actionId = null; this.actionType = null; });
        }
      });
    },
  },
};
</script>
